import apiClient from './api';
import i18n from '@/i18n';
import type { ReportDoc } from '@/components/ReportStudio/model';

/**
 * Reports saved in a workspace (geonexora-server ReportApi): gx.report documents holding the report studio's
 * definition — its tables, visuals, layout and the map as it was.
 */

export interface ReportSummary {
  id: string;
  workspace: string;
  name: string;
  kind: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  /** The version it is at; every save is a new one. */
  version: number;
  /** How many versions are kept. */
  versions: number;
  /** The definition's size in bytes (uncompressed), when known. */
  size?: number | null;
  format?: number | null;
  /** The address of the small map picture beside it (needs the token: read it with thumbnail()). */
  thumbnail?: string | null;
  canUpdate: boolean;
  canDelete: boolean;
}

export interface SavedReport extends ReportSummary {
  definition: ReportDoc;
  /** The newest version, when an older one was asked for. */
  latest?: number;
}

export interface ReportVersion {
  version: number;
  savedAt?: string | null;
  savedBy?: string | null;
  size?: number | null;
  format?: number | null;
  latest: boolean;
}

const base = (workspace: string) => `/v2/workspaces/${workspace}/reports`;

function reason(e: any): Error {
  const status = e?.response?.status;
  if (status === 413) return new Error(i18n.t('studio.save_too_large'));
  if (status === 403) return new Error(i18n.t('studio.save_forbidden'));
  const d = e?.response?.data;
  return new Error(d?.error ?? d?.detail ?? d?.message ?? e?.message ?? 'request failed');
}

/**
 * The server keeps every saved version of a report as a gzipped file and gives the definition back as JSON. Reports
 * saved for a short while before that may still hold a definition the browser packed itself
 * (`{format, packed: 'gzip-base64', data}`); the server unpacks those too, and so does this, should one come back.
 */
const PACKED = 'gzip-base64';

function fromBase64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function unpack(definition: any): Promise<ReportDoc> {
  if (!definition || definition.packed !== PACKED) return definition as ReportDoc;
  if (typeof DecompressionStream === 'undefined') throw new Error(i18n.t('studio.unknown_format'));
  const stream = new Blob([fromBase64(definition.data) as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return JSON.parse(await new Response(stream).text()) as ReportDoc;
}

export const reportService = {
  async list(workspace: string): Promise<ReportSummary[]> {
    try {
      return (await apiClient.get<ReportSummary[]>(base(workspace))).data;
    } catch (e) {
      throw reason(e);
    }
  },
  async get(workspace: string, id: string, version?: number): Promise<SavedReport> {
    try {
      const r = (await apiClient.get<SavedReport>(`${base(workspace)}/${id}`, {
        timeout: 120000,
        params: version ? { version } : undefined,
      })).data;
      return { ...r, definition: await unpack(r.definition) };
    } catch (e) {
      throw reason(e);
    }
  },
  async versions(workspace: string, id: string): Promise<ReportVersion[]> {
    try {
      return (await apiClient.get<ReportVersion[]>(`${base(workspace)}/${id}/versions`)).data;
    } catch (e) {
      throw reason(e);
    }
  },
  /** The small map picture as an object URL (revoke it when done), or nothing. */
  async thumbnail(report: ReportSummary): Promise<string | undefined> {
    if (!report.thumbnail) return undefined;
    try {
      const path = report.thumbnail.replace(/^\/api/, '');
      const blob = (await apiClient.get<Blob>(path, { responseType: 'blob' })).data;
      return URL.createObjectURL(blob);
    } catch {
      return undefined;
    }
  },
  async create(workspace: string, doc: ReportDoc, thumbnail?: string): Promise<SavedReport> {
    try {
      return (await apiClient.post<SavedReport>(base(workspace), { name: doc.name, kind: doc.kind, definition: doc, thumbnail }, { timeout: 300000 })).data;
    } catch (e) {
      throw reason(e);
    }
  },
  async update(workspace: string, id: string, doc: ReportDoc, thumbnail?: string): Promise<SavedReport> {
    try {
      return (await apiClient.put<SavedReport>(`${base(workspace)}/${id}`, { name: doc.name, definition: doc, thumbnail }, { timeout: 300000 })).data;
    } catch (e) {
      throw reason(e);
    }
  },
  async remove(workspace: string, id: string): Promise<void> {
    try {
      await apiClient.delete(`${base(workspace)}/${id}`);
    } catch (e) {
      throw reason(e);
    }
  },
};

export default reportService;
